import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SpreedlyCardFormHandle } from '../components-library/payment/spreedly/SpreedlyCardForm';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import ReduxActions from '../store/ReduxActions';
import { actionCreators } from '../store/actions';
import {
  DeliveryMode,
  ORDER_METHODS,
  PAYMENT_METHODS,
  PaymentGateway,
  PaymentMethod,
  PaypalVariant,
  requiresTaxpayerDocument,
} from '../services/exports/Constants';
import { CompanyDetailsInterface, PaymentOptionPropertiesInterface } from '../services/exports/Interfaces';
import {
  isPaymentMethodHiddenForOrderMethod,
  isRenderedOutsideStripeElements,
  isStripeDirectPaymentMethod,
  isStripeGooglePayInMobileApp,
  paymentMethodFromStripeType,
} from '../utils/payment-options-visibility';
import collect from 'collect.js';
import { useTranslation } from 'react-i18next';
import useMobileApp from '../hooks/mobile-app/useMobileApp';
import { CheckoutInfoBagHandle, SectionType } from '../components-library/checkout/sections/CheckoutInfoBag';
import useAuth from '../hooks/global/useAuth';
import usePaypalVariant from '../hooks/payment/usePaypalVariant';

interface Props {
  children: ReactElement;
}

interface PaymentFlowContextInterface {
  loading: boolean;
  hasOnlinePayments: boolean;
  onlinePaymentsGateway: PaymentGateway;
  secondaryGateway: PaymentGateway | null;
  getGatewayForMethod: (method: null | PaymentMethod) => PaymentGateway | null;
  /**
   * The wallets the native app says the device can pay with. Empty on the web: there the Express
   * Checkout Element (Stripe) or the Dropin (Adyen) offers the wallets the device and account take.
   */
  enabledWallets: PaymentMethod[];
  paymentOptions: PaymentOptionPropertiesInterface[];
  selectedPaymentMethod: null | PaymentMethod;
  paypalVariant: PaypalVariant | null;
  isGiftCardCheckout: boolean;
  setIsGiftCardCheckout: (value: boolean) => void;
  /**
   * The payer's CPF for a Brazilian method (Pix, NuPay), typed in the method's row and sent with
   * the checkout. Kept in memory only — never persisted with the rest of the payment flow.
   */
  customerDocument: string;
  setCustomerDocument: (value: string) => void;
  savedPaymentMethod: null | PaymentOptionPropertiesInterface;
  isDirectPayment: boolean;
  isWalletPayment: boolean;
  shouldSavePaymentInfo: boolean;
  hasSavedPaymentMethod: boolean;
  checkoutInfoBagRef: React.RefObject<CheckoutInfoBagHandle>;
  // The Spreedly card form mounts in the payment-method row (left column) while
  // the pay button lives in the summary column — the ref bridges the two, same
  // pattern as checkoutInfoBagRef.
  spreedlyCardFormRef: React.RefObject<SpreedlyCardFormHandle>;
  setSelectedPaymentMethod: (paymentMethod: null | PaymentMethod) => void;
  setShouldSavePaymentInfo: (value: boolean) => void;
  findPaymentMethod: (paymentMethod: PaymentMethod) => PaymentOptionPropertiesInterface;
  findSavedPaymentMethod: (paymentMethod: PaymentMethod) => PaymentOptionPropertiesInterface;
  isPaymentMethodSaved: (paymentMethod: PaymentMethod) => boolean;
  isPaymentMethodDisabled: (paymentMethod: PaymentMethod) => boolean;
  getPaymentOptions: (company: CompanyDetailsInterface) => PaymentOptionPropertiesInterface[];
  toggleCheckoutSection: (section: SectionType, open: boolean) => void;
  /**
   * Methods the charging gateway refused this session, e.g. PayPal on a Stripe account without
   * the capability. Withdrawn from the options so the selection moves on and the payment surface
   * can initialise, instead of waiting on an intent the gateway will never create.
   */
  unavailablePaymentMethods: PaymentMethod[];
  markPaymentMethodUnavailable: (paymentMethod: PaymentMethod) => void;
  /** Stripe Elements checkout: the card on file is what Pay charges, with its stored id, not the Element. */
  isSavedCardSelected: boolean;
  selectSavedCard: () => void;
  /** Stripe Elements checkout: the type of the Element's selected row, as Stripe names it (`card`, `klarna`, ...). */
  stripeElementType: string | null;
  selectStripeElementType: (type: string | null) => void;
}

export const PaymentFlowContext = React.createContext<PaymentFlowContextInterface>(null);

export default function PaymentFlowProvider(props: Props): ReactElement {
  const { t } = useTranslation(undefined, { keyPrefix: 'Providers:PaymentFlowProvider' });

  const { children } = props;

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const { payment_options } = useSelector((state: StoreInterface) => state.paymentOptions);
  const { selectedPaymentMethod } = useSelector((state: StoreInterface) => state.paymentFlow);

  const { isLoggedIn } = useAuth();
  const { wallets, isMobileApp } = useMobileApp();
  // Gift-card-purchase signal — set by the gift-card checkout's pay button (via
  // PaymentCoordinator/usePaymentFlow) and consumed by the PayPal router below.
  const [isGiftCardCheckout, setIsGiftCardCheckout] = useState(false);
  const [customerDocument, setCustomerDocument] = useState('');

  // The CPF is asked for one method and one checkout; switching to a method that needs none drops
  // it, so it is not kept in memory for longer than it is used.
  useEffect(() => {
    if (!requiresTaxpayerDocument(selectedPaymentMethod)) {
      setCustomerDocument('');
    }
  }, [selectedPaymentMethod]);
  const [unavailablePaymentMethods, setUnavailablePaymentMethods] = useState<PaymentMethod[]>([]);
  const markPaymentMethodUnavailable = useCallback((paymentMethod: PaymentMethod) => {
    setUnavailablePaymentMethods((current) =>
      current.includes(paymentMethod) ? current : [...current, paymentMethod],
    );
  }, []);
  const paypalVariant = usePaypalVariant(isGiftCardCheckout, unavailablePaymentMethods);
  const onlinePaymentsGateway = company?.payment_gateways?.[0] ?? null;
  const secondaryGateway = company?.payment_gateways?.[1] ?? null;

  const getGatewayForMethod = useCallback(
    (method: null | PaymentMethod): PaymentGateway | null => {
      if (!method || method === PaymentMethod.Cash || method === PaymentMethod.PaypalStandalone) {
        return null;
      }

      const gateways = company?.payment_gateways ?? (onlinePaymentsGateway ? [onlinePaymentsGateway] : []);

      if (method === PaymentMethod.Paypal) {
        // PayPal-direct is gateway-less (direct — money goes straight to the restaurant); only PayPal-via-Stripe routes
        // through Stripe.
        if (paypalVariant === PaypalVariant.Direct) {
          return null;
        }

        if (paypalVariant === PaypalVariant.ViaSpreedly) {
          return PaymentGateway.Spreedly;
        }

        return gateways.includes(PaymentGateway.Stripe) ? PaymentGateway.Stripe : null;
      }

      return onlinePaymentsGateway ?? null;
    },
    [company?.payment_gateways, onlinePaymentsGateway, paypalVariant],
  );

  const checkoutInfoBagRef = useRef<CheckoutInfoBagHandle>(null);
  const spreedlyCardFormRef = useRef<SpreedlyCardFormHandle>(null);
  const toggleCheckoutSection = (section: SectionType, open: boolean) =>
    checkoutInfoBagRef.current?.toggleSection(section, open);

  const enabledWallets = useMemo(
    () =>
      collect([])
        .when(isMobileApp && wallets.isApplePaySupported, (c) => c.push(PaymentMethod.ApplePay))
        .when(isMobileApp && wallets.isGooglePaySupported, (c) => c.push(PaymentMethod.GooglePay))
        .toArray() as PaymentMethod[],
    [isMobileApp, wallets.isApplePaySupported, wallets.isGooglePaySupported],
  );

  const [loading, setLoading] = useState(false);
  const [shouldSavePaymentInfo, setShouldSavePaymentInfo] = useState(true);
  // Stripe Elements checkout: the card on file is seated first; the Element's rows unseat it.
  const [prefersSavedCard, setPrefersSavedCard] = useState(true);
  const [stripeElementType, setStripeElementType] = useState<string | null>(null);
  // The same, readable at once. The Element reports its row from Stripe's postMessage, outside any
  // React event: the selection lands in Redux, which re-renders before this state catches up, and the
  // clean-up below would take the Element's own row for one it never confirmed.
  const stripeElementTypeRef = useRef<string | null>(null);

  const isDirectPayment = isStripeDirectPaymentMethod(company, selectedPaymentMethod);

  const selectedPaymentMethodRef = useRef(selectedPaymentMethod);
  selectedPaymentMethodRef.current = selectedPaymentMethod;

  // The loading flicker timer must not outlive the provider: firing after unmount is a
  // setState on a dead component, and in vitest it lands after the jsdom environment is torn
  // down ("window is not defined"), failing whichever test file happens to run last.
  const loadingResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (loadingResetTimer.current !== null) {
        clearTimeout(loadingResetTimer.current);
      }
    },
    [],
  );

  const setSelectedPaymentMethod = useCallback((paymentMethod: null | PaymentMethod) => {
    if (selectedPaymentMethodRef.current === paymentMethod) {
      return;
    }

    setLoading(true);
    ReduxActions.dispatch(actionCreators.paymentFlow.setSelectedPaymentMethod(paymentMethod));

    if (loadingResetTimer.current !== null) {
      clearTimeout(loadingResetTimer.current);
    }

    loadingResetTimer.current = setTimeout(() => setLoading(false), 5);
  }, []);

  const hasOnlinePayments = useMemo(
    () =>
      collect(company?.supported_payment_methods)
        .reject((item) => item === PaymentMethod.Cash)
        .isNotEmpty(),
    [company?.supported_payment_methods],
  );

  // Stripe checks out through the Payment Element and the Express Checkout Element on the restaurant's
  // own account: the Element lists the methods, `paymentOptions` keeps only the rows the shop draws.
  const isStripeCheckout = hasOnlinePayments && onlinePaymentsGateway === PaymentGateway.Stripe;
  const hasSavedStripeCard = useMemo(
    () => Boolean(collect(payment_options).firstWhere('payment_method_id', PaymentMethod.Card)),
    [payment_options],
  );

  const isPaymentOptionSupported = useCallback(
    (paymentOption: PaymentOptionPropertiesInterface, company: CompanyDetailsInterface) => {
      const hiddenForOrderMethods = company?.hidden_payment_methods
        ? (company?.hidden_payment_methods[paymentOption.payment_method_id] ?? [])
        : [];

      // The Payment Element lists the restaurant's Stripe methods itself; only the rows it does
      // not draw stay here (see isRenderedOutsideStripeElements).
      if (
        isStripeCheckout &&
        !isRenderedOutsideStripeElements(paymentOption.payment_method_id, {
          hasSavedCard: hasSavedStripeCard,
          isMobileApp,
        })
      ) {
        return false;
      }

      return (
        company?.supported_payment_methods?.includes(paymentOption.payment_method_id) &&
        !unavailablePaymentMethods.includes(paymentOption.payment_method_id) &&
        !isStripeGooglePayInMobileApp(
          paymentOption.payment_method_id,
          getGatewayForMethod(paymentOption.payment_method_id),
          isMobileApp,
        ) &&
        (paymentOption.payment_method_id !== PaymentMethod.ApplePay ||
          enabledWallets.includes(PaymentMethod.ApplePay)) &&
        (paymentOption.payment_method_id !== PaymentMethod.GooglePay ||
          enabledWallets.includes(PaymentMethod.GooglePay)) &&
        !hiddenForOrderMethods.includes('*') &&
        !hiddenForOrderMethods.includes(cached_order?.method)
      );
    },
    [
      enabledWallets,
      isMobileApp,
      getGatewayForMethod,
      cached_order?.method,
      unavailablePaymentMethods,
      company?.hidden_payment_methods,
      company?.supported_payment_methods,
      isStripeCheckout,
      hasSavedStripeCard,
    ],
  );

  const getPredefinedPaymentMethodName = (paymentMethodId: PaymentMethod): null | string => {
    if (paymentMethodId !== PaymentMethod.Cash) {
      return null;
    }

    if (company?.overwrite_cash_payments_to_invoice) {
      return t('labels.invoice');
    }

    if (company?.has_in_store_card_payments) {
      return t('labels.on_site');
    }

    return null;
  };

  const getPaymentOptions = useCallback(
    (company: CompanyDetailsInterface): PaymentOptionPropertiesInterface[] => {
      return collect(PAYMENT_METHODS())
        .reject((item: PaymentOptionPropertiesInterface) => !isPaymentOptionSupported(item, company))
        .map((item: PaymentOptionPropertiesInterface) => {
          const savedPaymentItem = collect(payment_options).firstWhere('payment_method_id', item.payment_method_id);

          return {
            ...item,
            ...(savedPaymentItem ?? {}),
            name: getPredefinedPaymentMethodName(item.payment_method_id) ?? savedPaymentItem?.name ?? item.name,
            display_name:
              getPredefinedPaymentMethodName(item.payment_method_id) ??
              savedPaymentItem?.display_name ??
              item.display_name,
            is_saved: !!savedPaymentItem,
            is_default: savedPaymentItem?.is_default === true,
          };
        })
        .toArray();
    },
    [payment_options, isPaymentOptionSupported],
  );

  const paymentOptions = useMemo<PaymentOptionPropertiesInterface[]>(() => {
    const options = getPaymentOptions(company);

    // A single "PayPal" entry appears whenever the router resolves a variant —
    // including direct-only restaurants where 'paypal' isn't in supported_payment_methods.
    // It must still respect the merchant's per-order-method hides (e.g. PayPal hidden on
    // pickup), otherwise this re-add resurrects a method `getPaymentOptions` just filtered
    // out — precisely because it was filtered out. See FOO-3176.
    if (
      paypalVariant &&
      !options.some((option) => option.payment_method_id === PaymentMethod.Paypal) &&
      !isPaymentMethodHiddenForOrderMethod(company?.hidden_payment_methods, PaymentMethod.Paypal, cached_order?.method)
    ) {
      const paypalOption = collect(PAYMENT_METHODS()).firstWhere('payment_method_id', PaymentMethod.Paypal);

      if (paypalOption) {
        options.push(paypalOption);
      }
    }

    return options;
  }, [
    payment_options,
    isPaymentOptionSupported,
    company?.overwrite_cash_payments_to_invoice,
    company?.hidden_payment_methods,
    cached_order?.method,
    paypalVariant,
    company,
  ]);

  const savedPaymentMethod = useMemo(
    () =>
      collect(paymentOptions)
        .sortBy('is_default')
        .first((item) => item.is_saved),
    [paymentOptions],
  );

  const savedCard = useMemo(
    () =>
      collect(paymentOptions).first((item) => item.is_saved && item.payment_method_id === PaymentMethod.Card) ?? null,
    [paymentOptions],
  );

  const isSavedCardSelected =
    isStripeCheckout && prefersSavedCard && Boolean(savedCard) && selectedPaymentMethod === PaymentMethod.Card;

  const selectSavedCard = useCallback(() => {
    setPrefersSavedCard(true);
    setSelectedPaymentMethod(PaymentMethod.Card);
  }, [setSelectedPaymentMethod]);

  // The Element's `change` event: its selected row becomes the checkout's method, so the draft,
  // the fees and the pay button follow it, and the card on file steps aside.
  const selectStripeElementType = useCallback(
    (type: string | null) => {
      stripeElementTypeRef.current = type;
      setStripeElementType(type);
      setPrefersSavedCard(false);

      const method = paymentMethodFromStripeType(type);

      if (method) {
        setSelectedPaymentMethod(method);
      }
    },
    [setSelectedPaymentMethod],
  );

  const findPaymentMethod = useCallback(
    (paymentMethod: PaymentMethod): PaymentOptionPropertiesInterface =>
      collect(paymentOptions).firstWhere('payment_method_id', paymentMethod),
    [paymentOptions],
  );

  const findSavedPaymentMethod = useCallback(
    (paymentMethod: PaymentMethod): PaymentOptionPropertiesInterface => {
      const result = findPaymentMethod(paymentMethod);

      return result?.is_saved ? result : null;
    },
    [paymentOptions],
  );

  const isPaymentMethodSaved = useCallback(
    (paymentMethod: PaymentMethod): boolean => findSavedPaymentMethod(paymentMethod) !== null,
    [paymentOptions],
  );

  const isPaymentMethodDisabled = useCallback(
    (paymentMethod: PaymentMethod): boolean =>
      paymentMethod === PaymentMethod.Cash &&
      cached_order?.method === ORDER_METHODS.DELIVERY &&
      company?.delivery_mode === DeliveryMode.External,
    [cached_order?.method, company?.delivery_mode],
  );

  // The card on file as the selection effect below last saw it, to tell a deleted card from none.
  const previousSavedCardRef = useRef(savedCard);

  // Seats a method for the gateways whose rows the shop draws — Stripe and Spreedly. Adyen's Dropin
  // owns its own stored-method selection, so it is left alone.
  useEffect(() => {
    const savedCardRemoved = Boolean(previousSavedCardRef.current) && !savedCard;
    previousSavedCardRef.current = savedCard;

    if (isStripeCheckout) {
      // The Element seats its own first row and reports it through selectStripeElementType. This
      // only seats the card on file, drops a selection the Element has not confirmed (a row from a
      // previous visit that is now the Element's, until it says so), and moves off a method this
      // order cannot take.
      if (!selectedPaymentMethod) {
        savedCard && selectSavedCard();

        return;
      }

      // A row of the shop's own this order no longer offers goes too: a persisted cash pick where the
      // restaurant takes no cash, or PayPal once Stripe refused it.
      const isUnofferedOwnRow =
        isRenderedOutsideStripeElements(selectedPaymentMethod, { hasSavedCard: Boolean(savedCard), isMobileApp }) &&
        !findPaymentMethod(selectedPaymentMethod);

      if (isPaymentMethodDisabled(selectedPaymentMethod) || isUnofferedOwnRow) {
        savedCard ? selectSavedCard() : setSelectedPaymentMethod(null);

        return;
      }

      // The card on file was taken (folding the Element) and is gone now, deleted from the list. The
      // Element's last row is not lit any more, and paying through it would submit a folded Element:
      // nothing is chosen, the Pay button disabled, until the customer picks a row again.
      if (savedCardRemoved && prefersSavedCard && selectedPaymentMethod === PaymentMethod.Card) {
        stripeElementTypeRef.current = null;
        setStripeElementType(null);
        setSelectedPaymentMethod(null);

        return;
      }

      if (
        stripeElementTypeRef.current === null &&
        !isRenderedOutsideStripeElements(selectedPaymentMethod, { hasSavedCard: Boolean(savedCard), isMobileApp })
      ) {
        setSelectedPaymentMethod(null);
      }

      return;
    }

    // Spreedly, and a Stripe restaurant that takes cash only: the saved (or first) method.
    if (
      (onlinePaymentsGateway === PaymentGateway.Stripe || onlinePaymentsGateway === PaymentGateway.Spreedly) &&
      (!selectedPaymentMethod ||
        !findPaymentMethod(selectedPaymentMethod) ||
        isPaymentMethodDisabled(selectedPaymentMethod))
    ) {
      setSelectedPaymentMethod(savedPaymentMethod?.payment_method_id ?? paymentOptions[0]?.payment_method_id);
    }
  }, [paymentOptions, onlinePaymentsGateway, isStripeCheckout, savedCard, stripeElementType]);

  // Adyen's Dropin keeps its own selection, so the effect above leaves it alone, but PayPal there is
  // a row of ours backed by Stripe. A persisted or refused PayPal selection with no variant left to
  // back it would still route the pay button to Stripe while StripeCoordinator is inactive (SHOP-WEB-2P).
  // Clear it: the customer picks again from what the Dropin and the PayPal row now offer.
  useEffect(() => {
    if (onlinePaymentsGateway !== PaymentGateway.Adyen || !selectedPaymentMethod) {
      return;
    }

    const isUnbackedPaypal = selectedPaymentMethod === PaymentMethod.Paypal && !paypalVariant;
    // Cash is persisted with the rest of the payment flow, so a pick from another order (or from
    // before the restaurant stopped taking cash) survives into one where cash is not offered, or is
    // disabled for external delivery. Sent as is, the backend refuses it with a 412 at the pay button.
    const isUnofferedCash =
      selectedPaymentMethod === PaymentMethod.Cash &&
      (!findPaymentMethod(PaymentMethod.Cash) || isPaymentMethodDisabled(PaymentMethod.Cash));

    if (isUnbackedPaypal || isUnofferedCash || unavailablePaymentMethods.includes(selectedPaymentMethod)) {
      setSelectedPaymentMethod(null);
    }
  }, [
    onlinePaymentsGateway,
    selectedPaymentMethod,
    paypalVariant,
    unavailablePaymentMethods,
    paymentOptions,
    isPaymentMethodDisabled,
  ]);

  const context = useMemo(
    () => ({
      loading,
      hasOnlinePayments,
      onlinePaymentsGateway,
      secondaryGateway,
      getGatewayForMethod,
      enabledWallets,
      paymentOptions,
      selectedPaymentMethod,
      paypalVariant,
      isGiftCardCheckout,
      setIsGiftCardCheckout,
      customerDocument,
      setCustomerDocument,
      savedPaymentMethod,
      isDirectPayment,
      isWalletPayment:
        selectedPaymentMethod === PaymentMethod.ApplePay || selectedPaymentMethod === PaymentMethod.GooglePay,
      // Consent the customer never saw must not be sent as granted: the checkbox is
      // hidden when the company cannot keep cards on file (Spreedly without stored
      // credentials). Absent flag (older payloads) keeps the previous behaviour.
      shouldSavePaymentInfo: shouldSavePaymentInfo && isLoggedIn && company?.saved_cards_enabled !== false,
      hasSavedPaymentMethod: Boolean(savedPaymentMethod),
      checkoutInfoBagRef,
      spreedlyCardFormRef,
      setSelectedPaymentMethod,
      setShouldSavePaymentInfo,
      findPaymentMethod,
      findSavedPaymentMethod,
      isPaymentMethodSaved,
      isPaymentMethodDisabled,
      getPaymentOptions,
      toggleCheckoutSection,
      unavailablePaymentMethods,
      markPaymentMethodUnavailable,
      isSavedCardSelected,
      selectSavedCard,
      stripeElementType,
      selectStripeElementType,
    }),
    [
      loading,
      hasOnlinePayments,
      onlinePaymentsGateway,
      secondaryGateway,
      getGatewayForMethod,
      enabledWallets,
      paymentOptions,
      selectedPaymentMethod,
      paypalVariant,
      isGiftCardCheckout,
      customerDocument,
      savedPaymentMethod,
      isDirectPayment,
      shouldSavePaymentInfo,
      isLoggedIn,
      enabledWallets,
      checkoutInfoBagRef,
      spreedlyCardFormRef,
      findPaymentMethod,
      findSavedPaymentMethod,
      isPaymentMethodSaved,
      isPaymentMethodDisabled,
      toggleCheckoutSection,
      unavailablePaymentMethods,
      markPaymentMethodUnavailable,
      isSavedCardSelected,
      selectSavedCard,
      stripeElementType,
      selectStripeElementType,
    ],
  );

  return <PaymentFlowContext.Provider value={context}>{children}</PaymentFlowContext.Provider>;
}
